import React, { FC, ComponentType } from 'react';
import { Navigate } from 'react-router';
import { Outlet, useLocation } from 'react-router-dom';

import { Account, AccountStatusId } from '@packages/models/api';
import { WebStorageKeys } from '@packages/core/storage';
import { useAuthState, AuthStatus } from '@packages/contexts/auth';
import { FeatureFlags, useFeatureFlags, useFeatureFlagsStatus } from '@packages/contexts/feature-flags';
import { getLogger } from '@packages/core/logging';

import { storageService } from '@web/services/singletons';
import { hasStisThirdPartyRole } from '@web/utils/can-view-stis';
import { usePublicationsState } from '@web/vin-sessions/components/stis-publications/context/publications.hooks';

const logger = getLogger('web');

const allowedThirdPartyPaths = [
    '/',
    '/stis',
    '/service-diagnostics',
    '/docs/',
    '/preview',
    '/onboard',
    '/stis/onboard',
];

interface PrivateRouteProps {
    path: string;
    routeGuard?: (account: Account | null, canAccessStis?: boolean) => boolean;
    fallback?: ComponentType;
    /** Hides the route behind a feature flag. When the flag is off the route redirects to the dashboard. */
    featureFlag?: keyof typeof FeatureFlags;
}
/**
 * Returning components for private routes are changes from v5 to v6. We can not use directly Routes which return from PrivateRoutes
 * so We have to set it in element property of Routes to private it. and if user successfully authenticated then return <Outlet/>
 */
export const PrivateRoute: FC<PrivateRouteProps> = ({ routeGuard, path, fallback, featureFlag }) => {
    const { account, status: authStatus } = useAuthState();
    const { canAccessStis } = usePublicationsState();
    const location = useLocation();
    const featureFlags = useFeatureFlags();
    const { isResolved: areFeatureFlagsResolved } = useFeatureFlagsStatus();
    const FallbackComponent = fallback || (() => <React.Fragment></React.Fragment>);

    const isThirdPartyUser = hasStisThirdPartyRole(account);
    if (
        isThirdPartyUser &&
        !allowedThirdPartyPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))
    ) {
        return <Navigate to="/" />;
    }

    // https://dev.to/iamandrewluca/private-route-in-react-router-v6-lg5
    if (path !== '/onboard' && account?.accountStatusId === AccountStatusId.New) {
        storageService.setItem(WebStorageKeys.AuthRedirectUrl, location);
        return <Navigate to="/onboard" />;
    }

    if (authStatus !== AuthStatus.Authenticated) {
        return <React.Fragment></React.Fragment>;
    }

    // Routes can opt into a feature flag. The flag reads off until Unleash answers, so the route waits
    // for that answer like it waits for the authentication above; once the flag is off for real the
    // route is hidden entirely, so a direct URL lands on the dashboard instead of exposing the feature.
    if (featureFlag && !areFeatureFlagsResolved) {
        return <React.Fragment></React.Fragment>;
    }

    if (featureFlag && !featureFlags[featureFlag]) {
        logger.info(`Redirecting away from a route hidden by a feature flag [${path}]`, {
            featureFlag,
            flagName: FeatureFlags[featureFlag],
            path,
        });
        return <Navigate replace to="/" />;
    }

    // pass account object to guard function
    // if it returns false, don't render route
    if (typeof routeGuard === 'function' && !routeGuard(account, canAccessStis)) {
        return <FallbackComponent />;
    }

    return <Outlet />;
};

export default PrivateRoute;
